import type { InvoiceDetailsReport, PaymentsCollectedReport } from "../../shared/reporting";

function total(values: readonly number[]) {
  if (values.some(value => !Number.isSafeInteger(value) || value < 0)) throw new Error("Unsupported report amount.");
  const sum = values.reduce((sum, value) => sum + BigInt(value), 0n);
  if (sum > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("Report total exceeds supported bounds. Choose a smaller period.");
  return Number(sum);
}
export interface MonthlyPerformance { month: string; issuedBillingMinor: number; recordedPaymentsMinor: number }
// Aggregate only returned authoritative report facts, never paid balances or
// imported source settlement. Dates retain the API's issue/received distinction.
export function businessPerformance(invoices: InvoiceDetailsReport, payments: PaymentsCollectedReport) {
  if (invoices.filters.from !== payments.filters.from || invoices.filters.to !== payments.filters.to) throw new Error("Report periods do not match.");
  if (total(invoices.rows.map(row => row.totalMinor)) !== invoices.summary.totalInvoicedMinor
    || total(payments.rows.map(row => row.amountMinor)) !== payments.summary.totalReceivedMinor) throw new Error("Report rows and totals do not reconcile.");
  const months = new Map<string, MonthlyPerformance>();
  const month = (date: string) => {
    const key = date.slice(0, 7);
    if (!months.has(key)) months.set(key, {month: key, issuedBillingMinor: 0, recordedPaymentsMinor: 0});
    return months.get(key)!;
  };
  for (const invoice of invoices.rows) { const row = month(invoice.issueDate); row.issuedBillingMinor = total([row.issuedBillingMinor, invoice.totalMinor]); }
  for (const payment of payments.rows) { const row = month(payment.receivedDate); row.recordedPaymentsMinor = total([row.recordedPaymentsMinor, payment.amountMinor]); }
  const rows = [...months.values()].sort((a, b) => a.month.localeCompare(b.month));
  const highest = rows.reduce((highest, row) => Math.max(highest, row.issuedBillingMinor), 0);
  const strongest = rows.filter(row => highest > 0 && row.issuedBillingMinor === highest);
  return {rows, strongest, issuedBillingMinor: invoices.summary.totalInvoicedMinor, recordedPaymentsMinor: payments.summary.totalReceivedMinor};
}
export function monthName(month: string) { return new Intl.DateTimeFormat("en-US", {month: "long", year: "numeric", timeZone: "UTC"}).format(new Date(`${month}-01T00:00:00Z`)); }
export function monthPeriod(month: string, period: {from: string; to: string}) {
  const endDate = new Date(`${month}-01T00:00:00Z`);
  endDate.setUTCMonth(endDate.getUTCMonth() + 1, 0);
  const end = endDate.toISOString().slice(0, 10);
  return {from: `${month}-01` < period.from ? period.from : `${month}-01`, to: end > period.to ? period.to : end};
}
